import { createContext, useContext, useState, useEffect } from "react";
import api from "../services/api";

const AuthContext = createContext(null);

const authFallback = {
  user: null,
  loading: false,
  isAuthenticated: false,
  sessionType: null,
  login: async () => ({ success: false, error: "AuthProvider missing" }),
  adminLogin: async () => ({ success: false, error: "AuthProvider missing" }),
  register: async () => ({ success: false, error: "AuthProvider missing" }),
  logout: async () => {},
  sendEmailOTP: async () => ({
    success: false,
    error: "AuthProvider missing",
  }),
  sendPhoneOTP: async () => ({
    success: false,
    error: "AuthProvider missing",
  }),
  verifyOTP: async () => ({ success: false, error: "AuthProvider missing" }),
  updateUser: async () => ({ success: false, error: "AuthProvider missing" }),
  refreshUser: async () => null,
  syncUser: async () => null,
  googleLogin: async () => ({ success: false, error: "AuthProvider missing" }),
  forgotPassword: async () => ({
    success: false,
    error: "AuthProvider missing",
  }),
  verifyForgotPasswordOTP: async () => ({
    success: false,
    error: "AuthProvider missing",
  }),
  resetPassword: async () => ({
    success: false,
    error: "AuthProvider missing",
  }),
  changePassword: async () => ({
    success: false,
    error: "AuthProvider missing",
  }),
  hasRole: () => false,
  isAdmin: () => false,
  isAdminSession: () => false,
  isUserSession: () => false,
  hasPermission: () => false,
};

// User roles hierarchy
export const USER_ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  MODERATOR: "moderator",
  EDITOR: "editor",
  VIEWER: "viewer",
  USER: "user",
};

// Role hierarchy for permission checking
const ROLE_HIERARCHY = {
  super_admin: 5,
  admin: 4,
  moderator: 3,
  editor: 2,
  viewer: 1,
  user: 0,
};

const SESSION_TYPES = {
  ADMIN: "admin",
  USER: "user",
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.error(
      "useAuth used outside AuthProvider. Falling back to defaults.",
    );
    return authFallback;
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionType, setSessionType] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedUser = localStorage.getItem("denizns_user");
        const token = localStorage.getItem("denizns_token");
        const storedSessionType = localStorage.getItem("denizns_session_type");
        const parsedStoredUser = storedUser ? JSON.parse(storedUser) : null;

        if (storedUser && token) {
          // Verify token is still valid by fetching current user
          try {
            const response = await api.get("/auth/me");
            if (response.success && response.data?.user) {
              const isStaffSession = storedSessionType
                ? storedSessionType === SESSION_TYPES.ADMIN
                : parsedStoredUser?.isStaff === true ||
                  response.data.user?.isStaff === true;
              const userData = formatUserData(
                response.data.user,
                isStaffSession,
              );
              localStorage.setItem("denizns_user", JSON.stringify(userData));
              localStorage.setItem(
                "denizns_session_type",
                isStaffSession ? SESSION_TYPES.ADMIN : SESSION_TYPES.USER,
              );
              setUser(userData);
              setSessionType(
                isStaffSession ? SESSION_TYPES.ADMIN : SESSION_TYPES.USER,
              );
              setIsAuthenticated(true);
            } else {
              // Token invalid, clear storage
              logout();
            }
          } catch {
            // Token invalid or account restricted/deleted
            logout();
          }
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Format user data from API response
  const formatUserData = (apiUser, isStaff = false) => {
    if (isStaff) {
      return {
        id: apiUser.id,
        name: `${apiUser.first_name || apiUser.firstName || ""} ${apiUser.last_name || apiUser.lastName || ""}`.trim(),
        firstName: apiUser.first_name || apiUser.firstName,
        lastName: apiUser.last_name || apiUser.lastName,
        email: apiUser.email,
        phone: apiUser.phone,
        department: apiUser.department,
        avatar: apiUser.avatar,
        role: apiUser.role || USER_ROLES.ADMIN,
        isStaff: true,
        permissions: apiUser.permissions || ["all"],
      };
    }

    return {
      id: apiUser.id,
      denNumber: apiUser.den_number || apiUser.denNumber,
      name: `${apiUser.first_name || apiUser.firstName || ""} ${apiUser.last_name || apiUser.lastName || ""}`.trim(),
      firstName: apiUser.first_name || apiUser.firstName,
      lastName: apiUser.last_name || apiUser.lastName,
      email: apiUser.email,
      phone: apiUser.phone,
      subscription: apiUser.subscription || "free",
      profileCompletion:
        apiUser.profile_completion || apiUser.profileCompletion || 0,
      isVerified: apiUser.is_verified || apiUser.isVerified || false,
      isEmailVerified:
        apiUser.is_email_verified || apiUser.isEmailVerified || false,
      avatar: apiUser.avatar,
      role: apiUser.role || USER_ROLES.USER,
      status: apiUser.status || "pending", // 'active', 'pending', 'suspended' - for activation tracking
      profession: apiUser.profession,
      experience: apiUser.experience,
      location: apiUser.location,
      bio: apiUser.bio,
      skills: apiUser.skills || [],
      languages: apiUser.languages || [],
      education: apiUser.education || [],
      work_experience: apiUser.work_experience || apiUser.workExperience || [],
      certifications: apiUser.certifications || [],
      dateOfBirth: apiUser.date_of_birth || apiUser.dateOfBirth,
      nationality: apiUser.nationality,
      citizenship: apiUser.citizenship,
      visaStatus: apiUser.visa_status || apiUser.visaStatus,
      visaExpiry: apiUser.visa_expiry || apiUser.visaExpiry,
      needsSponsorship: apiUser.needs_sponsorship || apiUser.needsSponsorship,
      englishProficiency:
        apiUser.english_proficiency || apiUser.englishProficiency,
      countryOfResidence:
        apiUser.country_of_residence || apiUser.countryOfResidence,
      city: apiUser.city,
      referrals: apiUser.referrals || [],
      isStaff: false,
      permissions: [],
    };
  };

  // Check if user has a specific role
  const hasRole = (requiredRole) => {
    if (!user || !user.role) return false;
    return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[requiredRole];
  };

  // Check if user is admin (admin or super_admin)
  const isAdmin = () => {
    return hasRole(USER_ROLES.ADMIN) || user?.isStaff;
  };

  const isAdminSession = () => {
    return sessionType === SESSION_TYPES.ADMIN && isAdmin();
  };

  const isUserSession = () => {
    return sessionType === SESSION_TYPES.USER && !user?.isStaff;
  };

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === USER_ROLES.SUPER_ADMIN) return true;
    if (user.permissions?.includes("all")) return true;
    return user.permissions?.includes(permission) || false;
  };

  // Login with email/password. The unified backend endpoint resolves either a
  // user OR a staff/admin account in a single request and returns `isStaff`, so
  // we set the session type from that flag rather than assuming a user session.
  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });

      if (response.success && response.data) {
        const { token, isStaff = false } = response.data;
        const session = isStaff ? SESSION_TYPES.ADMIN : SESSION_TYPES.USER;

        localStorage.setItem("denizns_token", token);
        localStorage.setItem("denizns_session_type", session);

        // Fetch full profile immediately after login
        const meResponse = await api.get("/auth/me");
        const apiUser =
          meResponse.success && meResponse.data?.user
            ? meResponse.data.user
            : response.data.staff || response.data.user;

        const userData = formatUserData(apiUser, isStaff);
        localStorage.setItem("denizns_user", JSON.stringify(userData));
        setUser(userData);
        setSessionType(session);
        setIsAuthenticated(true);

        return { success: true, user: userData };
      }

      return { success: false, error: response.message || "Login failed" };
    } catch (error) {
      console.error("Login failed:", error);
      return { success: false, error: error.message || "Login failed" };
    }
  };

  // Admin login. The unified backend endpoint prefers the staff account but may
  // resolve a plain user (someone with only a user account signing in here), so
  // we honor the returned `isStaff` flag to set the correct session type.
  const adminLogin = async (email, password) => {
    try {
      const response = await api.post("/auth/admin/login", { email, password });

      if (response.success && response.data) {
        const { token, isStaff = true } = response.data;
        const session = isStaff ? SESSION_TYPES.ADMIN : SESSION_TYPES.USER;

        localStorage.setItem("denizns_token", token);
        localStorage.setItem("denizns_session_type", session);

        // Fetch full profile immediately after login
        const meResponse = await api.get("/auth/me");
        const apiUser =
          meResponse.success && meResponse.data?.user
            ? meResponse.data.user
            : response.data.staff || response.data.user;

        const userData = formatUserData(apiUser, isStaff);
        localStorage.setItem("denizns_user", JSON.stringify(userData));
        setUser(userData);
        setSessionType(session);
        setIsAuthenticated(true);

        return { success: true, user: userData };
      }

      return { success: false, error: response.message || "Login failed" };
    } catch (error) {
      console.error("Admin login failed:", error);
      return { success: false, error: error.message || "Login failed" };
    }
  };

  // Register new user
  const register = async (userData) => {
    try {
      const response = await api.post("/auth/register", {
        first_name: userData.firstName || userData.name?.split(" ")[0],
        last_name:
          userData.lastName || userData.name?.split(" ").slice(1).join(" "),
        email: userData.email,
        phone: userData.phone,
        phone_country_code: userData.phoneCountryCode,
        password: userData.password,
        referredBy: userData.referredBy,
        staffRef: userData.staffRef,
        usagePurpose: userData.usagePurpose,
        heardAbout: userData.heardAbout,
        hasUkDrivingLicense: userData.hasUkDrivingLicense,
      });

      if (response.success && response.data) {
        const { token } = response.data;

        localStorage.setItem("denizns_token", token);
        localStorage.setItem("denizns_session_type", SESSION_TYPES.USER);

        // Fetch full profile immediately after register
        const meResponse = await api.get("/auth/me");
        const apiUser =
          meResponse.success && meResponse.data?.user
            ? meResponse.data.user
            : response.data.user;

        const formattedUser = formatUserData(apiUser, false);
        localStorage.setItem("denizns_user", JSON.stringify(formattedUser));
        setUser(formattedUser);
        setSessionType(SESSION_TYPES.USER);
        setIsAuthenticated(true);

        return { success: true, user: formattedUser };
      }

      return {
        success: false,
        error: response.message || "Registration failed",
      };
    } catch (error) {
      console.error("Registration failed:", error);
      return { success: false, error: error.message || "Registration failed" };
    }
  };

  // Google login
  const googleLogin = async (token, referredBy, staffRef) => {
    console.log(
      "AuthContext: Initiating googleLogin with token length:",
      token?.length,
    );
    try {
      const response = await api.post("/auth/google", {
        token,
        referredBy,
        staffRef,
      });
      console.log("AuthContext: googleLogin API response:", response);

      if (response.success && response.data) {
        // googleAuth may resolve an existing staff/employee account. Honor the
        // returned isStaff flag so an employee signing in with Google gets an
        // admin session (and the staff payload), not a plain user session.
        const { token: accessToken, isStaff = false } = response.data;
        const apiUser = isStaff
          ? response.data.staff || response.data.user
          : response.data.user;
        const session = isStaff ? SESSION_TYPES.ADMIN : SESSION_TYPES.USER;
        console.log("AuthContext: googleLogin success, user:", apiUser?.email);
        const userData = formatUserData(apiUser, isStaff);
        console.log("AuthContext: Formatted user data:", userData);

        localStorage.setItem("denizns_token", accessToken);
        localStorage.setItem("denizns_user", JSON.stringify(userData));
        localStorage.setItem("denizns_session_type", session);

        setUser(userData);
        setSessionType(session);
        setIsAuthenticated(true);

        return { success: true, user: userData };
      }

      console.warn("AuthContext: googleLogin failed with response:", response);
      return {
        success: false,
        error: response.message || "Google login failed",
      };
    } catch (error) {
      console.error("AuthContext: googleLogin exception:", error);
      return { success: false, error: error.message || "Google login failed" };
    }
  };

  // Send OTP to email
  const sendEmailOTP = async (email) => {
    try {
      const response = await api.post("/auth/send-otp", {
        identifier: email,
        email,
        type: "email",
      });
      return { success: response.success, message: response.message };
    } catch (error) {
      console.error("Send email OTP failed:", error);
      return { success: false, error: error.message };
    }
  };

  // Send OTP to phone
  const sendPhoneOTP = async (phone) => {
    try {
      const response = await api.post("/auth/send-otp", {
        phone,
        type: "phone",
      });
      return { success: response.success, message: response.message };
    } catch (error) {
      console.error("Send phone OTP failed:", error);
      return { success: false, error: error.message };
    }
  };

  // Verify OTP
  const verifyOTP = async (type, otp, identifier) => {
    try {
      const response = await api.post("/auth/verify-otp", {
        type,
        otp,
        identifier,
        ...(type === "email" ? { email: identifier } : { phone: identifier }),
      });

      if (response.success) {
        // Refresh user data after verification
        const meResponse = await api.get("/auth/me");
        if (meResponse.success && meResponse.data?.user) {
          const userData = formatUserData(meResponse.data.user, user?.isStaff);
          localStorage.setItem("denizns_user", JSON.stringify(userData));
          setUser(userData);
        }
      }

      return { success: response.success, message: response.message };
    } catch (error) {
      console.error("OTP verification failed:", error);
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await api.post("/auth/logout", {});
    } catch {
      // Ignore logout API errors
    }
    localStorage.removeItem("denizns_user");
    localStorage.removeItem("denizns_token");
    localStorage.removeItem("denizns_session_type");
    setUser(null);
    setSessionType(null);
    setIsAuthenticated(false);
  };

  // Update user profile
  const updateUser = async (updates) => {
    try {
      const response = await api.put("/auth/profile", updates);

      if (response.success && response.data?.user) {
        const userData = formatUserData(response.data.user, user?.isStaff);
        localStorage.setItem("denizns_user", JSON.stringify(userData));
        setUser(userData);
        return { success: true, user: userData };
      }

      return { success: false, error: response.message };
    } catch (error) {
      console.error("Profile update failed:", error);
      return { success: false, error: error.message };
    }
  };

  // Refresh user data from API (GET)
  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      if (response.success && response.data?.user) {
        const userData = formatUserData(response.data.user, user?.isStaff);
        localStorage.setItem("denizns_user", JSON.stringify(userData));
        setUser(userData);
        return userData;
      }
    } catch (error) {
      console.error("Refresh user failed:", error);
    }
    return null;
  };

  // Sync user data from API (POST)
  const syncUser = async () => {
    try {
      const response = await api.post("/auth/sync");
      if (response.success && response.data?.user) {
        const userData = formatUserData(response.data.user, user?.isStaff);
        localStorage.setItem("denizns_user", JSON.stringify(userData));
        setUser(userData);
        return userData;
      }
    } catch (error) {
      console.error("Sync user failed:", error);
    }
    return null;
  };

  // Forgot password - send reset OTP
  const forgotPassword = async (email) => {
    try {
      const response = await api.post("/auth/forgot-password", { email });
      return { success: response.success, message: response.message };
    } catch (error) {
      console.error("Forgot password failed:", error);
      return {
        success: false,
        error: error.message || "Failed to send reset code",
      };
    }
  };

  // Verify forgot-password OTP and get temporary reset token
  const verifyForgotPasswordOTP = async (email, otp) => {
    try {
      const response = await api.post("/auth/forgot-password/verify-otp", {
        email,
        otp,
      });
      return {
        success: response.success,
        message: response.message,
        resetToken: response.data?.resetToken,
      };
    } catch (error) {
      console.error("Forgot OTP verification failed:", error);
      return {
        success: false,
        error: error.message || "Failed to verify OTP",
      };
    }
  };

  // Reset password with OTP
  const resetPassword = async (email, resetToken, newPassword) => {
    try {
      const response = await api.post("/auth/reset-password", {
        email,
        resetToken,
        newPassword,
      });
      return { success: response.success, message: response.message };
    } catch (error) {
      console.error("Reset password failed:", error);
      return {
        success: false,
        error: error.message || "Failed to reset password",
      };
    }
  };

  // Change password (authenticated)
  const changePassword = async (oldPassword, newPassword) => {
    try {
      const response = await api.post("/auth/change-password", {
        oldPassword,
        newPassword,
      });
      return { success: response.success, message: response.message };
    } catch (error) {
      console.error("Change password failed:", error);
      return {
        success: false,
        error: error.message || "Failed to change password",
      };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    sessionType,
    login,
    adminLogin,
    register,
    logout,
    sendEmailOTP,
    sendPhoneOTP,
    verifyOTP,
    updateUser,
    refreshUser,
    syncUser,
    googleLogin,
    forgotPassword,
    verifyForgotPasswordOTP,
    resetPassword,
    changePassword,
    hasRole,
    isAdmin,
    isAdminSession,
    isUserSession,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
